import React from "react";
import { useTranslation } from "react-i18next";

import { Button, Space, Table as AntTable, Row, Col, Typography } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

import { API_URL } from "../../shared/utils/Util";
import { getFetchT, shortDisplayTime } from "../../shared/utils/TypedUtil";
import { ListShippingOrdersResponse } from "../../types/api";
import { OrderBlurb } from "../../types/model";
import { OrderDetailModal } from "./OrderDetailModal";
import { useAntdTheme, useNotification } from "../../shared/hooks";

const { Text } = Typography;

type OrderTabProps = {
  restaurantId: string;

  // pending, started, done
  state: string;
};

const PAGE_SIZE = 50;

type PageCache = {
  orders: OrderBlurb[];
  nextToken: string | null;
};

export const OrderTab = (props: OrderTabProps) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { notifyError } = useNotification();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [pageCache, setPageCache] = React.useState<Map<number, PageCache>>(new Map());

  const fetchPage = (pageNumber: number, pageToken: string | null = null) => {
    setLoading(true);
    const page_param = pageToken ? `&page=${pageToken}` : "";
    getFetchT<ListShippingOrdersResponse>(
      `${API_URL}/v1/r/${props.restaurantId}/shipping_orders?limit=${PAGE_SIZE}&state=${props.state}${page_param}`,
      (result: ListShippingOrdersResponse) => {
        setPageCache((prev) => {
          const newCache = new Map(prev);
          newCache.set(pageNumber, {
            orders: result.orders || [],
            nextToken: result.next_page || null,
          });
          return newCache;
        });
        setCurrentPage(pageNumber);
        setLoading(false);
      },
      (err) => {
        notifyError(t("failed_to_fetch_data"), err);
        setLoading(false);
      }
    );
  };

  React.useEffect(() => {
    // Reset cache and fetch first page when state changes
    setPageCache(new Map());
    setCurrentPage(1);
    fetchPage(1, null);
  }, [props.state]);

  const handleNextPage = () => {
    const nextPageNum = currentPage + 1;

    // Check if we already have this page cached
    if (pageCache.has(nextPageNum)) {
      setCurrentPage(nextPageNum);
      return;
    }

    // Fetch new page using the next token from current page
    const currentPageData = pageCache.get(currentPage);
    if (currentPageData?.nextToken) {
      fetchPage(nextPageNum, currentPageData.nextToken);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const currentPageData = pageCache.get(currentPage);
  const orders = currentPageData?.orders || [];
  const hasNextPage = !!currentPageData?.nextToken;
  const hasPreviousPage = currentPage > 1;

  const columns: any = [
    {
      title: t("name"),
      dataIndex: "pos_order_name",
      key: "pos_order_name",
      render: (text: string, record: OrderBlurb) => {
        return (
          <OrderDetailModal
            restaurantId={props.restaurantId}
            order_id={record.id}
            title={record.pos_order_name}
          />
        );
      },
    },
    {
      title: t("create_time"),
      dataIndex: "create_time",
      key: "create_time",
      render: (text: string) => shortDisplayTime(new Date(text)),
    },
    {
      title: t("action"),
      key: "action",
      render: (text: string, record: OrderBlurb) => (
        <Space size="middle">
          <Button type="link">{t("shipping_label")}</Button>
          <Button type="link">{t("packing_list")}</Button>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <AntTable
        columns={columns}
        dataSource={orders}
        rowKey={(r: OrderBlurb) => r.id}
        loading={loading}
        pagination={false}
        style={{ borderRadius: token.borderRadiusLG }}
      />

      <Row gutter={[16, 16]} align="middle" justify="space-between">
        <Col>
          <Button
            icon={<LeftOutlined />}
            onClick={handlePreviousPage}
            disabled={!hasPreviousPage || loading}
            size="large"
            style={{ borderRadius: token.borderRadiusLG }}
          >
            {t("previous")}
          </Button>
        </Col>
        <Col>
          <Text strong style={{ fontSize: token.fontSizeLG }}>
            {t("page")} {currentPage}
            {hasNextPage && " • "}
            {hasNextPage && (
              <Text type="secondary" style={{ fontSize: token.fontSize }}>
                {orders.length} {t("items")}
              </Text>
            )}
            {!hasNextPage && orders.length > 0 && (
              <Text type="secondary" style={{ fontSize: token.fontSize }}>
                {" • "}
                {(currentPage - 1) * PAGE_SIZE + 1}-{(currentPage - 1) * PAGE_SIZE + orders.length}{" "}
                {t("items")}
              </Text>
            )}
          </Text>
        </Col>
        <Col>
          <Button
            icon={<RightOutlined />}
            onClick={handleNextPage}
            disabled={!hasNextPage || loading}
            size="large"
            style={{ borderRadius: token.borderRadiusLG }}
          >
            {t("next")}
          </Button>
        </Col>
      </Row>
    </Space>
  );
};
